import { html, nothing } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsElement } from '../PdsElement';
import styles from './secondary-navigation-level-one.scss?inline';
import '../main-nav-item/main-nav-item';
import '../secondary-navigation-level-two/secondary-navigation-level-two';

/**
 * @summary This component generates the level one navigation within the secondary navigation, and generates a main-nav-item at desktop and either a collapsible or a link (depending on props) at mobile.
 *
 * @slot default One or more pds-secondary-navigation-level-two elements
 */
@customElement('pds-secondary-navigation-level-one', {
  category: 'component',
  type: 'component',
  styles,
})
export class PdsSecondaryNavigationLevelOne extends PdsElement {
  /**
   * The title of the top tier item (whether a parent or link)
   */
  @property({ type: String, reflect: true })
  title: string = '';

  /**
   * If the top tier item is a link, the href for it
   */
  @property({ reflect: true })
  href: string;

  /**
   * However many columns the items under a parent should have at desktop
   */
  @property({ reflect: true })
  columns: '1col' | '2col' | '3col' | '4col' = '1col';

  /**
   * @internal
   * Set to true for visual representation of the user's current section
   * If not set, component logic will determine the value by matching the window location's href to the nested links' href properties
   */
  @property({ reflect: true })
  activeSection: boolean = false;

  /**
   * This grabs the secondary-nav-level-two items
   * @internal
   */
  @queryAssignedElements({ slot: undefined })
  defaultSlotElements: HTMLElement[];

  /**
   * @internal
   * Function to determine if this is the active section
   * This function looks at the url of each secondary nav level two item
   * and determines if the current URL matches the nav item URL
   * This is the default behavior of the activeSectionCallback prop
   *
   */
  determineActiveSection(): boolean {
    let returnValue = false;

    this.defaultSlotElements.forEach((element: HTMLElement) => {
      if (element.tagName === 'PDS-SECONDARY-NAVIGATION-LEVEL-TWO') {
        if (window.location.href === element.getAttribute('href')) {
          returnValue = true;
        }
      }
    });
    return returnValue;
  }

  /**
   * @internal
   * Callback function to determine if this is the active section
   * It MUST return a boolean
   * Defaults to looking at the url of each secondary nav level two item
   * and determines if the current URL matches the nav item URL
   */
  @property()
  activeSectionCallback: Function = this.determineActiveSection;

  /**
   * @internal
   */
  firstUpdated(): void {
    this.activeSection = this.activeSectionCallback();
  }

  /**
   * @internal
   * Returns the markup to be used at mobile breakpoints
   */
  generateNavOneItemMarkupMobile() {
    if (!this.href) {
      return html`<pds-collapsible
        variant="secondary-nav"
        class="${this.classEl('menu-item-section')}"
        ><span slot="summary-title">${this.title}</span>
        <span slot="collapsible-content"
          >${this.getDynamicSlotMarkup()}</span
        ></pds-collapsible
      >`;
    }
    return html`<pds-link
      class="${this.classEl('menu-item-section')}"
      href="${this.href}"
      ariaLabel="${this.title}"
      ariaCurrent="${this.activeSection ? 'true' : nothing}"
      >${this.title}</pds-link
    >`;
  }

  /**
   * @internal
   * Returns the markup to be used at large breakpoints
   */
  generateNavOneItemMarkupDesktop() {
    if (!this.href) {
      return html`<pds-main-nav-item
        dropdown
        activeSection="${this.activeSection || nothing}"
        class="${this.classEl('menu-item')}"
        text="${this.title}"
      >
        <span class="${this.classMod(this.columns)}">
          ${this.getDynamicSlotMarkup()}</span
        ></pds-main-nav-item
      >`;
    }
    return html`<pds-main-nav-item
      activeSection="${this.activeSection || nothing}"
      class="${this.classEl('menu-item')}"
      href="${this.href}"
      text="${this.title}"
    ></pds-main-nav-item>`;
  }

  render() {
    return html`<div class="${this.getClass()}">
      <slot
        hidden
        @slotchange="${(e: Event) => {
          this.duplicateSlotMarkup(e.target as HTMLSlotElement);
        }}"
        class="${this.classEl('slot')}"
      ></slot>
      <div class="${this.classMod('mobile')}">
        ${this.generateNavOneItemMarkupMobile()}
      </div>
      <div class="${this.classMod('desktop')}">
        ${this.generateNavOneItemMarkupDesktop()}
      </div>
    </div>`;
  }
}
